import { App } from 'antd';
import { t } from 'i18next';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { QueryKey } from '@tanstack/react-query';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import {
  FanguardApiContractsInteractionInteractionResponseModel,
  FanguardApiContractsInteractionInteractionTypeModelEnumKeys,
} from 'src/api/generated';

export const buildQueryParams = (params: Record<string, string | number | boolean | null | undefined>) => {
  const urlParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      urlParams.append(key, value.toString());
    }
  });
  return urlParams.toString();
};

export type EntityType =
  | 'connection'
  | 'client'
  | 'campaign'
  | 'site'
  | 'ticketType'
  | 'ticketGroup'
  | 'emailTemplate'
  | 'person'
  | 'people'
  | 'user'
  | 'venue'
  | 'sensorGroup'
  | 'sensor'
  | 'controller'
  | 'list'
  | 'settings'
  | 'asset'
  | 'ticket'
  | 'event'
  | 'photo'
  | 'device'
  | 'report'
  | 'image'
  | 'snapshot'
  | 'zone'
  | 'serviceKey';

export type ActionType =
  | 'create'
  | 'update'
  | 'delete'
  | 'upload'
  | 'add'
  | 'remove'
  | 'reset'
  | 'unlock'
  | 'optOut'
  | 'sync'
  | 'generate'
  | 'export'
  | 'refresh'
  | 'activate'
  | 'deactivate';

export interface MessageConfig<TData = unknown> {
  entityType: EntityType;
  action: ActionType;
  customSuccessMessage?: string | ((data: TData, t: TFunction) => string);
  customErrorMessage?: boolean | ((error: unknown, t: TFunction) => string | undefined);
  shouldSuppressErrorMessage?: (error: unknown) => boolean;
  customEntityName?: string;
  /** Query keys (arrays) or string keys to invalidate on success. Accepts readonly arrays from query-key-factory. */
  queryKeysToInvalidate?: Array<QueryKey | string | readonly unknown[]>;
  onSuccess?: (data: TData) => void;
  onError?: () => void;
}

const getEntityTranslationKey = (entityType: EntityType): string => {
  const entityMap: Record<EntityType, string> = {
    settings: 'responseMessages.types.settings',
    connection: 'responseMessages.types.connection',
    site: 'responseMessages.types.site',
    campaign: 'responseMessages.types.campaign',
    emailTemplate: 'responseMessages.types.emailTemplate',
    ticketType: 'responseMessages.types.ticketType',
    ticketGroup: 'responseMessages.types.ticketGroup',
    person: 'responseMessages.types.person',
    people: 'responseMessages.types.people',
    user: 'responseMessages.types.user',
    venue: 'responseMessages.types.venue',
    event: 'responseMessages.types.event',
    controller: 'responseMessages.types.controller',
    sensor: 'responseMessages.types.sensor',
    sensorGroup: 'responseMessages.types.sensorGroup',
    list: 'responseMessages.types.list',
    asset: 'responseMessages.types.asset',
    ticket: 'responseMessages.types.ticket',
    photo: 'responseMessages.types.photo',
    device: 'responseMessages.types.device',
    client: 'responseMessages.types.client',
    report: 'responseMessages.types.report',
    image: 'responseMessages.types.image',
    snapshot: 'responseMessages.types.snapshot',
    zone: 'responseMessages.types.zone',
    serviceKey: 'responseMessages.types.serviceKey',
  };
  return entityMap[entityType] || 'general.item';
};

const getActionTranslationKey = (action: ActionType): string => {
  const actionMap: Record<ActionType, string> = {
    create: 'responseMessages.created',
    update: 'responseMessages.updated',
    delete: 'responseMessages.deleted',
    add: 'responseMessages.added',
    remove: 'responseMessages.removed',
    sync: 'responseMessages.synced',
    upload: 'general.uploadSuccess',
    reset: 'general.resetSuccess',
    unlock: 'general.unlockSuccess',
    optOut: 'general.optOutSuccess',
    generate: 'responseMessages.generated',
    export: 'responseMessages.exported',
    refresh: 'responseMessages.refreshed',
    activate: 'responseMessages.activated',
    deactivate: 'responseMessages.deactivated',
  };
  return actionMap[action] || 'general.success';
};

const getErrorActionTranslationKey = (action: ActionType): string => {
  const errorActionMap: Record<ActionType, string> = {
    create: 'responseMessages.unableToCreate',
    update: 'responseMessages.unableToUpdate',
    delete: 'responseMessages.unableToDelete',
    upload: 'responseMessages.unableToUpload',
    add: 'responseMessages.unableToAdd',
    remove: 'responseMessages.unableToRemove',
    sync: 'responseMessages.unableToSync',
    reset: 'responseMessages.resetError',
    unlock: 'responseMessages.unlockError',
    optOut: 'responseMessages.optOutError',
    generate: 'responseMessages.unableToGenerate',
    export: 'responseMessages.unableToExport',
    refresh: 'responseMessages.unableToRefresh',
    activate: 'responseMessages.unableToActivate',
    deactivate: 'responseMessages.unableToDeactivate',
  };
  return errorActionMap[action] || 'responseMessages.error';
};

const generateSuccessMessage = <TData = unknown>(config: MessageConfig<TData>, t: TFunction, data: TData): string => {
  if (typeof config.customSuccessMessage === 'function') {
    return config.customSuccessMessage(data, t);
  }
  if (config.customSuccessMessage) {
    return config.customSuccessMessage;
  }

  const entityKey = getEntityTranslationKey(config.entityType);
  const actionKey = getActionTranslationKey(config.action);

  return `${t(entityKey)} ${t(actionKey)}`;
};

const generateErrorMessage = <TData = unknown>(config: MessageConfig<TData>, t: TFunction): string => {
  const entityKey = getEntityTranslationKey(config.entityType);
  const actionKey = getErrorActionTranslationKey(config.action);

  return `${t(actionKey)} ${t(entityKey)}.`;
};

export const useMutationWithMessages = <TData = unknown, TError = Error, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  config: MessageConfig<TData>,
): UseMutationResult<TData, TError, TVariables> => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      if (config.queryKeysToInvalidate) {
        config.queryKeysToInvalidate.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] });
        });
      }

      const successMessage = generateSuccessMessage(config, t, data);
      message.success(successMessage);

      if (config.onSuccess) {
        config.onSuccess(data);
      }
    },
    onError: (data: TError & { response: { data: { message: string } } }) => {
      if (config.shouldSuppressErrorMessage?.(data)) {
        return;
      }

      const errorMessage = generateErrorMessage(config, t);
      if (typeof config.customErrorMessage === 'function') {
        message.error(config.customErrorMessage(data, t) ?? errorMessage);
      } else if (config.customErrorMessage) {
        message.error(data?.response?.data?.message ?? errorMessage);
      } else {
        message.error(errorMessage);
      }
    },
  });
};

export const getStatusText = (
  interaction: FanguardApiContractsInteractionInteractionResponseModel | undefined | null,
) => {
  if (!interaction) {
    return t('statusText.unknown');
  }
  switch (interaction.grantStatus) {
    case 'Granted':
      return t('statusText.granted');
    case 'Pending':
      return t('statusText.pending');
    case 'Partial':
      return t('statusText.partial');
    case 'Denied':
      if (!interaction?.isAffirmed) {
        switch (interaction?.overrideReason?.toLowerCase()) {
          case 'deniedspoofattempt':
            return t('statusText.deniedSpoofAttempt');
          case 'deniedsystemerror':
            return t('statusText.deniedSystemError');
          default:
            return t('statusText.deniedUnmatched');
        }
      } else {
        if (interaction.overrideReason == null) {
          return t('statusText.denied');
        }
        switch (interaction?.overrideReason?.toLowerCase()) {
          case 'deniedunderage':
            return t('statusText.deniedUnderage');
          case 'deniednocredential':
            return t('statusText.deniedNoCredential');
          case 'deniedmultiplecredentials':
            return t('statusText.deniedMultipleCredentials');
          case 'deniedblur':
            return t('statusText.deniedBlur');
          case 'deniedsystemerror':
            return t('statusText.deniedSystemError');
          case 'deniedconflictingmatches':
            return t('statusText.deniedConflictingMatches');
          case 'deniedinvalidticket':
            return t('statusText.deniedInvalidTicket');
          case 'deniedbadconfiguration':
            return t('statusText.deniedBadConfiguration');
          case 'timedouttransactionexpired':
            return t('statusText.timedOutTransactionExpired');
          case 'deniedorderfulfillment':
            if (interaction.overrideReasonDetail == null) {
              return t('statusText.deniedOrderFulfillment');
            }
            switch (interaction.overrideReasonDetail?.toLowerCase()) {
              case 'deniedorderfulfillment':
                return t('statusText.deniedOrderFulfillment');
              case 'no_entry':
                return t('statusText.deniedNoEntry');
              case 'no_card_on_file':
                return t('statusText.deniedNoCardOnFile');
              case 'pre_auth_failed':
                return t('statusText.deniedPreAuthFailed');
              case 'age_restriction_failed':
                return t('statusText.deniedAgeRestrictionFailed');
              default:
                return t('statusText.deniedUnmatched');
            }
          case 'deniedcredential':
            if (interaction.overrideReasonDetail == null) {
              return t('statusText.deniedUnknown');
            }
            switch (interaction?.overrideReasonDetail?.toLowerCase()) {
              case 'nolinkedarea':
                return t('statusText.deniedNoLinkedArea');
              case 'nolinkedaccount':
                return t('statusText.deniedNoLinkedAccount');
              case 'unknown':
                return t('statusText.deniedUnknownResponse');
              case 'error':
                return t('statusText.deniedUnknownError');
              case 'badgenotfound':
                return t('statusText.deniedCredentialNotFound');
              case 'badgedeactivated':
                return t('statusText.deniedCredentialDeactivated');
              case 'registrationnotfoundatevent':
                return t('statusText.deniedCredentialNotFoundAtEvent');
              case 'registrationnotapproved':
                return t('statusText.deniedCredentialRegistrationNotApproved');
              case 'noprivileges':
                return t('statusText.deniedCredentialNotValidAtThisArea');
              case 'registrationsubtypenotallowed':
                return t('statusText.deniedCredentialBlockedAtThisArea');
              case 'areanotfound':
                return t('statusText.deniedSensorAreaNotFound');
              case 'areanotactive':
                return t('statusText.deniedSensorAreaNotActive');
              case 'areanoconfigurations':
                return t('statusText.deniedSensorAreaNotConfigured');
              case 'alreadycheckedin':
                return t('statusText.deniedAlreadyCheckedIn');
              case 'alreadyentered':
                return t('statusText.deniedAlreadyEntered');
              case 'sectionrestriction':
                return t('statusText.deniedSectionRestriction');
              default:
                return t('statusText.deniedUnmatched');
            }
          case 'deniedwebview':
            if (interaction.overrideReasonDetail == null) {
              return t('statusText.deniedUnknown');
            }
            switch (interaction?.overrideReasonDetail?.toLowerCase()) {
              case 'no_linked_account':
                return t('statusText.deniedNoLinkedAccount');
              case 'invalid_configuration':
                return t('statusText.deniedBadConfiguration');
              default:
                return t('statusText.deniedUnmatched');
            }
          default:
            return t('statusText.deniedUnmatched');
        }
      }
    default:
      return 'Unknown';
  }
};

export const showInteractionLabel = (
  type: FanguardApiContractsInteractionInteractionTypeModelEnumKeys | undefined | null,
) => {
  if (!type) {
    return false;
  }
  return (
    type === 'InteractionDecisionAccessControl' ||
    type === 'InteractionDecisionCredentialing' ||
    type === 'InteractionDecisionWebView'
  );
};
